import type { PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import type { HubGridRow } from '../../../../@types/oneButtonType';
import {
    checklistFromFinancePartnerSyncRow,
    findSyncStatusRowForLeadOrProduct,
    financePartnerSyncStoreKey,
    isPartnerSyncCompleteForConfig,
    partnerSkipsTitleCheck,
    EMPTY_PARTNER_SYNC_CHECKLIST,
} from './partnerSyncConfig';
import {
    hideBatteryLightreachPartnerSync,
    lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts,
} from './productsFinanceHubHelpers';
import { normalizeFinanceGridPartner } from './utils/finance/financePartnerNormalize';

function opportunityDetailsNested(od: any) {
    return od?.OpportunityDetails ?? od?.opportunityDetails;
}

function getOpportunityProductRows(od: any): any[] {
    if (!od) return [];
    const n = opportunityDetailsNested(od);
    const pick = od.OpportunityProduct ?? od.opportunityProduct ?? n?.OpportunityProduct ?? n?.opportunityProduct;
    return Array.isArray(pick) ? pick : [];
}

function getFinancePartnerLeadList(od: any): any[] {
    if (!od) return [];
    const nested = opportunityDetailsNested(od);
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        nested?.FinancePartnerLeadList ??
        nested?.financePartnerLeadListModels ??
        nested?.financePartnerLeadList ??
        [];
    return Array.isArray(list) ? list : [];
}

function partnerNameFromOpportunityProduct(row: any): string {
    return String(
        row?.Finance_Partner_Display_Name ??
            row?.FinancePartnerDisplayName ??
            row?.Finance_Partner ??
            row?.finance_Partner ??
            '',
    ).trim();
}

function toCanonicalPartnerKeyFromProductRow(raw: string): string {
    return normalizeFinanceGridPartner(String(raw || '').trim());
}

function partnerNameFromLeadRecord(row: any): string {
    return String(
        row?.FinancePartnerName ??
            row?.Finance_Partner_Name ??
            row?.Finance_Partner ??
            row?.financePartnerName ??
            '',
    ).trim();
}

function leadIdFromRecord(row: any): string {
    return String(
        row?.Finance_Partner_Sync_LeadID ??
            row?.Finance_Partner_Sync_LeadId ??
            row?.FinancePartnerSyncLeadId ??
            row?.finance_Partner_Sync_LeadID ??
            '',
    ).trim();
}

function normalizeProductTypeKey(type: string | null | undefined): string {
    return String(type || '')
        .trim()
        .toLowerCase()
        .replace(/\s+only$/, '');
}

function leadRecordMatchesPartner(row: any, partnerName: string): boolean {
    const nm = partnerNameFromLeadRecord(row);
    if (!nm) return false;
    return toCanonicalPartnerKeyFromProductRow(nm) === toCanonicalPartnerKeyFromProductRow(partnerName);
}

function leadRecordProductType(row: any): string {
    return String(
        row?.ProductTypeName ??
            row?.productTypeName ??
            row?.Product_Type_Name ??
            row?.lead_Type ??
            row?.Lead_Type ??
            '',
    ).trim();
}

function leadRecordMatchesProductType(row: any, productName: string): boolean {
    const selected = normalizeProductTypeKey(productName);
    if (!selected) return true;
    const leadType = normalizeProductTypeKey(leadRecordProductType(row));
    if (!leadType) return false;
    return leadType === selected || leadType.includes(selected) || selected.includes(leadType);
}

function leadRecordTimestamp(row: any): number {
    const raw =
        row?.LastModifiedDate ??
        row?.lastModifiedDate ??
        row?.CreatedDate ??
        row?.createdDate ??
        '';
    const parsed = Date.parse(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
}

function pickLatestLead(rows: any[]): any | null {
    const withIds = rows.filter((row: any) => leadIdFromRecord(row));
    if (withIds.length === 0) return null;
    const dated = withIds.filter((row: any) => leadRecordTimestamp(row) > 0);
    if (dated.length > 0) {
        return dated.reduce((latest: any, row: any) =>
            leadRecordTimestamp(row) >= leadRecordTimestamp(latest) ? row : latest,
        );
    }
    return withIds[withIds.length - 1];
}

/** Current finance-partner Lead Id for this partner (and product, when provided). */
export function getSyncedLeadIdForPartner(od: any, partnerName: string, productName?: string): string | null {
    if (!od) return null;
    const partnerKey = toCanonicalPartnerKeyFromProductRow(partnerName);
    const selectedProduct = String(productName || '').trim();
    const leadList = getFinancePartnerLeadList(od);

    let matchingLeads = leadList.filter((row: any) => leadRecordMatchesPartner(row, partnerName));
    if (selectedProduct) {
        matchingLeads = matchingLeads.filter((row: any) => leadRecordMatchesProductType(row, selectedProduct));
    }

    const latest = pickLatestLead(matchingLeads);
    if (latest) return leadIdFromRecord(latest);

    for (const row of getOpportunityProductRows(od)) {
        if (row?.ActiveFlag === false || row?.activeFlag === false) continue;
        if (toCanonicalPartnerKeyFromProductRow(partnerNameFromOpportunityProduct(row)) !== partnerKey) continue;
        if (selectedProduct) {
            const rowType = String(row.Product_Type_Name ?? row.product_Type_Name ?? '').trim();
            if (!leadRecordMatchesProductType({ ProductTypeName: rowType }, selectedProduct)) continue;
        }
        const id = leadIdFromRecord(row);
        if (id) return id;
    }

    return null;
}

export function readFinancePartnerSyncStatusFromStore(
    statusMap: Record<string, PartnerSyncChecklist | undefined>,
    partnerName: string,
    productTypeName?: string | null,
): PartnerSyncChecklist | undefined {
    const syncKey = normalizeFinanceGridPartner(partnerName);
    const storeKey = financePartnerSyncStoreKey(syncKey, productTypeName);
    return statusMap[storeKey] ?? statusMap[syncKey] ?? statusMap[partnerName];
}

/**
 * Same merge rules as FinancePartnerChecklistModal: API false must not clear store true;
 * lead is true when API or a synced lead id exists.
 */
export function effectivePartnerSyncChecklistForUi(input: {
    partnerName: string;
    productTypeName?: string | null;
    opportunityDetails: any;
    rawStatuses: any[];
    statusMap: Record<string, PartnerSyncChecklist | undefined>;
}): PartnerSyncChecklist {
    const { partnerName, productTypeName, opportunityDetails, rawStatuses, statusMap } = input;
    const syncedLeadId = getSyncedLeadIdForPartner(opportunityDetails, partnerName, productTypeName ?? undefined);
    const matchingRow = findSyncStatusRowForLeadOrProduct(rawStatuses, {
        leadId: syncedLeadId,
        productTypeName: productTypeName ?? undefined,
    });
    const row =
        matchingRow ??
        (!productTypeName && Array.isArray(rawStatuses) && rawStatuses.length > 0
            ? rawStatuses[0]
            : undefined);
    const fromRow = checklistFromFinancePartnerSyncRow(row);
    const fromStore = readFinancePartnerSyncStatusFromStore(statusMap, partnerName, productTypeName) ?? {};

    let titleCheck = fromRow.titleCheck === true || fromStore.titleCheck === true;
    if (partnerSkipsTitleCheck(partnerName)) titleCheck = true;

    return {
        ...EMPTY_PARTNER_SYNC_CHECKLIST,
        ...fromStore,
        ...fromRow,
        leadSyncAction: fromStore.leadSyncAction ?? null,
        lead: fromRow.lead === true || !!syncedLeadId || fromStore.lead === true,
        contact: fromRow.contact === true || fromStore.contact === true,
        utility: fromRow.utility === true || fromStore.utility === true,
        leadSystem: fromRow.leadSystem === true || fromStore.leadSystem === true,
        titleCheck,
        creditCheck: fromRow.creditCheck === true || fromStore.creditCheck === true,
    };
}

export function buildProductPartnerChecklistStatus(input: {
    partnerName: string;
    productTypeName: string;
    opportunityDetails: any;
    rawStatuses: any[];
    statusMap: Record<string, PartnerSyncChecklist | undefined>;
}): PartnerSyncChecklist {
    return effectivePartnerSyncChecklistForUi({
        partnerName: input.partnerName,
        productTypeName: input.productTypeName,
        opportunityDetails: input.opportunityDetails,
        rawStatuses: input.rawStatuses,
        statusMap: input.statusMap,
    });
}

function activeProductTypesForPartnerMilestone(
    syncKey: string,
    opportunityDetails: any,
    hubRows?: HubGridRow[],
): string[] {
    const partnerKey = normalizeFinanceGridPartner(syncKey);
    const rows = getOpportunityProductRows(opportunityDetails);
    const seen = new Set<string>();
    const out: string[] = [];

    for (const row of rows) {
        if (row?.ActiveFlag === false || row?.activeFlag === false) continue;
        const raw = partnerNameFromOpportunityProduct(row);
        if (!raw) continue;
        if (toCanonicalPartnerKeyFromProductRow(raw) !== partnerKey) continue;
        const pt = String(row.Product_Type_Name ?? row.product_Type_Name ?? '').trim();
        if (pt && !seen.has(pt)) {
            seen.add(pt);
            out.push(pt);
        }
    }

    if (out.length === 0) return out;

    const products = rows;
    const singleSbLead = lightreachSolarBatteryRequireSingleLeadFromOpportunityProducts(products);
    const hideBatterySync = hubRows?.length ? hideBatteryLightreachPartnerSync(hubRows) : singleSbLead;

    if (partnerKey === 'LightReach' && (singleSbLead || hideBatterySync)) {
        return out.filter((pt) => pt !== 'Battery');
    }

    return out;
}

/** Status & Milestones capsule: every required product line must pass partner checklist config. */
export function isPartnerSyncMilestoneComplete(input: {
    syncKey: string;
    opportunityDetails: any;
    hubRows?: HubGridRow[];
    financePartnerSyncStatus: Record<string, PartnerSyncChecklist | undefined>;
    rawStatuses: any[];
}): boolean {
    const { syncKey, opportunityDetails, hubRows, financePartnerSyncStatus, rawStatuses } = input;
    const productTypes = activeProductTypesForPartnerMilestone(syncKey, opportunityDetails, hubRows);
    if (productTypes.length === 0) return false;

    for (const productTypeName of productTypes) {
        const status = buildProductPartnerChecklistStatus({
            partnerName: syncKey,
            productTypeName,
            opportunityDetails,
            rawStatuses: Array.isArray(rawStatuses) ? rawStatuses : [],
            statusMap: financePartnerSyncStatus,
        });
        if (!isPartnerSyncCompleteForConfig(syncKey, status)) return false;
    }
    return true;
}
